import { useState, useEffect, useRef } from 'react';
import '@/App.css';
import './workspace.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'sonner';
import AuthScreen from './components/AuthScreen';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import WizardV3 from './components/WizardV3';
import ProjectView from './components/ProjectView';
import Profile from './components/Profile';
import AdminConsole from './components/AdminConsole';
import Billing from './components/Billing';
import Notifications from './components/Notifications';
import OnboardingTour from './components/OnboardingTour';
import HelpCenter from './components/HelpCenter';
import ProductTour, { hasTourForView, shouldShowTour } from './components/ProductTour';
import AssistantWidget from './components/AssistantWidget';
import { HELP_EVENT } from './components/ContextHelpButton';
import SocialConnectReminderModal from './components/SocialConnectReminderModal';
import PurchaseComplete from './components/PurchaseComplete';
import PurchaseActivate from './components/PurchaseActivate';
import PublicHelpCenter from './components/PublicHelpCenter';
import FunnyPublicLanding from './components/FunnyPublicLanding';
import { PRIMARY_PROJECT_VIEWS, resolveProjectView } from './lib/legacyProjectNav';

const PROJECT_VIEWS = PRIMARY_PROJECT_VIEWS;
const LEGACY_PROJECT_VIEWS = ['project', 'list', 'calendar', 'personas', 'creative', 'design', 'library', 'social', 'pro'];
const RESTORABLE_VIEWS = ['dashboard', 'wizard', ...PROJECT_VIEWS, ...LEGACY_PROJECT_VIEWS, 'profile', 'admin', 'billing', 'notifications'];
const GLOBAL_VIEWS = new Set(['dashboard', 'wizard', 'profile', 'admin', 'billing', 'notifications']);

function navStorageKey(userKey, suffix) {
  return `sketchario:${userKey}:${suffix}`;
}

function AppContent() {
  const { user, loading, api, effectivePlan, planReady } = useAuth();
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('dashboard');
  const [projectSubTabHint, setProjectSubTabHint] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [wizardResumeData, setWizardResumeData] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [helpArticleId, setHelpArticleId] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [socialReminderProject, setSocialReminderProject] = useState(null);
  const hydratedUserKeyRef = useRef(null);
  const userStorageKey = user?._id || user?.email || null;

  useEffect(() => {
    if (!userStorageKey || hydratedUserKeyRef.current === userStorageKey) return;
    hydratedUserKeyRef.current = userStorageKey;
    try {
      const storedView = localStorage.getItem(navStorageKey(userStorageKey, 'activeView'));
      const storedProject = localStorage.getItem(navStorageKey(userStorageKey, 'selectedProject'));
      if (storedProject) setSelectedProject(JSON.parse(storedProject));
      if (storedView && RESTORABLE_VIEWS.includes(storedView)) {
        if ([...PROJECT_VIEWS, ...LEGACY_PROJECT_VIEWS].includes(storedView)) {
          const resolved = resolveProjectView(storedView);
          setActiveView(resolved.view);
          if (resolved.subTab) setProjectSubTabHint(resolved.subTab);
        } else {
          setActiveView(storedView);
        }
      }
    } catch {}
  }, [userStorageKey]);

  useEffect(() => {
    if (!userStorageKey || hydratedUserKeyRef.current !== userStorageKey) return;
    try {
      localStorage.setItem(navStorageKey(userStorageKey, 'activeView'), activeView);
      if (selectedProject) localStorage.setItem(navStorageKey(userStorageKey, 'selectedProject'), JSON.stringify(selectedProject));
      else localStorage.removeItem(navStorageKey(userStorageKey, 'selectedProject'));
    } catch {}
  }, [userStorageKey, activeView, selectedProject]);

  useEffect(() => {
    if (!hydratedUserKeyRef.current) return;
    if (PROJECT_VIEWS.includes(activeView) && !selectedProject) setActiveView('dashboard');
  }, [activeView, selectedProject]);

  useEffect(() => {
    const isCurrentlyProjectView = PROJECT_VIEWS.includes(activeView) || LEGACY_PROJECT_VIEWS.includes(activeView);
    if (!isCurrentlyProjectView || !selectedProject?.id || selectedProject.social_reminder_shown) return;
    let cancelled = false;
    api.get(`/social/project/${selectedProject.id}`)
      .then(r => {
        if (!cancelled && (r.data || []).length === 0) setSocialReminderProject(selectedProject);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeView, selectedProject, api]);

  useEffect(() => {
    if (user && api) {
      api.get('/onboarding/status').then(r => {
        setOnboardingStatus(r.data || {});
        if (!r.data.completed) setShowOnboarding(true);
      }).catch(() => setOnboardingStatus({}));
    }
  }, [user, api]);

  useEffect(() => {
    if (!user || !hasTourForView(activeView)) {
      setShowTour(false);
      return;
    }
    if (activeView === 'dashboard' && onboardingStatus === null) return;
    setShowTour(shouldShowTour(onboardingStatus, activeView));
  }, [activeView, onboardingStatus, user]);

  useEffect(() => {
    const openContextHelp = (event) => {
      setHelpArticleId(event?.detail?.articleId || null);
      setShowHelp(true);
    };
    window.addEventListener(HELP_EVENT, openContextHelp);
    return () => window.removeEventListener(HELP_EVENT, openContextHelp);
  }, []);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      const plan = effectivePlan || user.plan || 'creator';
      const planLabel = plan === 'strategist' ? 'Strategist' : plan === 'creator' ? 'Creator' : 'base';
      toast.success(`🎉 Benvenuto in Sketchario ${planLabel}! Il tuo piano è attivo.`, {
        duration: 8000,
        description: 'Sei pronto a creare contenuti. Inizia dal Wizard!',
      });
    }
  }, [user, effectivePlan]);

  if (loading || (user && planReady === false)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-[var(--gradient-start)] to-[var(--gradient-end)] animate-pulse" />
          <p className="text-[var(--text-secondary)] text-sm">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const params = new URLSearchParams(window.location.search);
    const authIntent =
      window.location.pathname === '/login'
      || params.has('reset_token')
      || params.has('verify_email')
      || params.get('billing') === 'success';
    return authIntent ? <AuthScreen /> : <FunnyPublicLanding />;
  }

  const trial = effectivePlan === 'free' ? user?.free_trial : null;
  const trialExpired = Boolean(trial?.enabled && (trial.expired || Date.now() >= Date.parse(trial.expires_at)));
  const trialDaysLeft = trial?.enabled ? Math.max(0, Math.ceil((Date.parse(trial.expires_at) - Date.now()) / 86400000)) : 0;
  const isProjectView = PROJECT_VIEWS.includes(activeView) || LEGACY_PROJECT_VIEWS.includes(activeView);
  const handleSetActiveView = view => {
    if (GLOBAL_VIEWS.has(view)) {
      setActiveView(view);
      setProjectSubTabHint(null);
      return;
    }
    const resolved = resolveProjectView(view);
    const isProjectDestination = PROJECT_VIEWS.includes(resolved.view);
    if (isProjectDestination && !selectedProject) return;
    setActiveView(resolved.view);
    setProjectSubTabHint(resolved.subTab);
  };

  const dismissSocialReminder = async goToConnections => {
    const project = socialReminderProject;
    setSocialReminderProject(null);
    if (!project) return;
    try { await api.put(`/projects/${project.id}`, { social_reminder_shown: true }); } catch {}
    setSelectedProject(prev => (prev && prev.id === project.id) ? { ...prev, social_reminder_shown: true } : prev);
    if (goToConnections) handleSetActiveView('connections');
  };

  const renderView = () => {
    if (LEGACY_PROJECT_VIEWS.includes(activeView)) {
      const resolved = resolveProjectView(activeView);
      return (
        <ProjectView
          project={selectedProject}
          setActiveView={setActiveView}
          activeTab={resolved.view}
          initialSubTab={projectSubTabHint || resolved.subTab}
        />
      );
    }
    switch (activeView) {
      case 'dashboard':
        return <Dashboard setActiveView={setActiveView} setSelectedProject={setSelectedProject} setWizardResumeData={setWizardResumeData} />;
      case 'wizard':
        return <WizardV3 setActiveView={setActiveView} setSelectedProject={setSelectedProject} resumeData={wizardResumeData} setWizardResumeData={setWizardResumeData} />;
      case 'dna':
        return <ProjectView project={selectedProject} setActiveView={setActiveView} activeTab="dna" initialSubTab={projectSubTabHint} />;
      case 'content':
      case 'media':
      case 'connections':
      case 'analytics':
      case 'feeds':
        return (
          <ProjectView
            project={selectedProject}
            setActiveView={setActiveView}
            activeTab={activeView}
            initialSubTab={projectSubTabHint}
          />
        );
      case 'profile':
        return <Profile />;
      case 'admin':
        return <AdminConsole />;
      case 'billing':
        return <Billing />;
      case 'notifications':
        return <Notifications />;
      default:
        return <Dashboard setActiveView={setActiveView} setSelectedProject={setSelectedProject} />;
    }
  };

  return (
    <div className="main-layout">
      <Sidebar activeView={activeView} setActiveView={handleSetActiveView} isProjectView={isProjectView} onHelpOpen={() => { setHelpArticleId(null); setShowHelp(true); }} selectedProject={selectedProject} />
      <main className="main-content flex flex-col">
        {trial?.enabled && (
          <div className="flex-shrink-0 px-4 md:px-8 py-3 border-b border-[var(--border-color)] flex flex-wrap items-center justify-between gap-3" style={{ background: 'var(--bg-secondary)' }} role="status" data-testid="free-trial-banner">
            <p className="text-sm text-[var(--text-secondary)]">
              {trialExpired ? t('freeTrial.bannerExpired') : t('freeTrial.bannerActive', { count: trialDaysLeft })}
            </p>
            <button type="button" className="btn-gradient text-sm" onClick={() => handleSetActiveView('billing')}>
              {t('freeTrial.upgrade')}
            </button>
          </div>
        )}
        <div className={`flex-1 min-h-0 ${isProjectView ? 'overflow-hidden' : 'p-4 md:p-8 overflow-y-auto overscroll-contain'}`}>{renderView()}</div>
        {!isProjectView && (
          <footer className="flex-shrink-0 px-4 md:px-8 py-3 md:py-4 border-t border-[var(--border-color)] flex flex-col sm:flex-row items-center justify-between gap-2" style={{ background: 'var(--bg-secondary)' }}>
            <p className="text-[10px] md:text-[11px] text-[var(--text-muted)]">{t('footer.rights', { year: new Date().getFullYear() })}</p>
            <div className="flex gap-3 md:gap-4">
              <a href="https://www.sketchario.app/legal/termini-e-condizioni.html" target="_blank" rel="noopener" className="text-[10px] md:text-[11px] text-[var(--text-muted)] hover:text-white transition-colors">{t('footer.terms')}</a>
              <a href="https://www.sketchario.app/legal/privacy-policy.html" target="_blank" rel="noopener" className="text-[10px] md:text-[11px] text-[var(--text-muted)] hover:text-white transition-colors">{t('footer.privacy')}</a>
              <a href="https://www.sketchario.app/legal/cookie-policy.html" target="_blank" rel="noopener" className="text-[10px] md:text-[11px] text-[var(--text-muted)] hover:text-white transition-colors">{t('footer.cookie')}</a>
              <a href="https://www.sketchario.app/refund-request.html" target="_blank" rel="noopener" className="text-[10px] md:text-[11px] text-[var(--text-muted)] hover:text-white transition-colors">{t('footer.withdrawal')}</a>
            </div>
          </footer>
        )}
      </main>
      {socialReminderProject && (
        <SocialConnectReminderModal
          onDismiss={() => dismissSocialReminder(false)}
          onGoToConnections={() => dismissSocialReminder(true)}
        />
      )}
      {showOnboarding && <OnboardingTour onComplete={() => setShowOnboarding(false)} />}
      {showHelp && <HelpCenter
        initialArticleId={helpArticleId}
        activeView={activeView}
        onStartTour={() => {
          if (!hasTourForView(activeView)) return;
          setShowHelp(false);
          setHelpArticleId(null);
          setShowTour(true);
        }}
        onClose={() => { setShowHelp(false); setHelpArticleId(null); }}
      />}
      {showTour && !showHelp && !showOnboarding && hasTourForView(activeView) && (
        <ProductTour api={api} activeView={activeView} onFinish={() => setShowTour(false)} />
      )}
      <AssistantWidget activeView={activeView} selectedProject={selectedProject} />
    </div>
  );
}

function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/purchase/complete" element={<PurchaseComplete />} />
            <Route path="/purchase/activate" element={<PurchaseActivate />} />
            <Route path="/help" element={<PublicHelpCenter />} />
            <Route path="/landing-funny-01" element={<Navigate to="/" replace />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors toastOptions={{ duration: 4000 }} />
    </div>
  );
}

export default App;
